import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API } from "@/context/AuthContext";
import { Bell, BellRing } from "lucide-react";
import { useTranslation } from "react-i18next";
import { enablePush, pushPermission, pushSupported } from "@/lib/push";
import { toast } from "sonner";

export default function NotificationBell({ placement = "down" }) {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [perm, setPerm] = useState(pushPermission());

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/notifications`);
      setItems(data.items || []);
      setUnread(data.unread || 0);
    } catch (e) { console.error("Meldingen laden mislukt:", e); }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, [load]);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && unread > 0) {
      try { await axios.post(`${API}/notifications/read-all`); setUnread(0); } catch (e) { console.error("Meldingen als gelezen markeren mislukt:", e); }
    }
  };

  const doEnable = async () => {
    try {
      await enablePush(API, axios);
      setPerm("granted");
      toast.success(t("notif.enabled"));
    } catch (e) {
      toast.error(e.message === "denied" ? t("notif.denied") : t("notif.enableFailed"));
    }
  };

  const doTest = async () => {
    try {
      await axios.post(`${API}/push/test`);
      toast.success(t("notif.testSent"));
      load();
    } catch (e) {
      toast.error(t("notif.testFailed"));
    }
  };

  return (
    <div className="relative">
      <button onClick={toggle} className="relative p-2 rounded-sm hover:bg-zinc-100" data-testid="notif-bell" aria-label={t("notif.title")}>
        {unread > 0 ? <BellRing className="w-5 h-5 text-[#0052FF]" /> : <Bell className="w-5 h-5 text-zinc-600" />}
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center" data-testid="notif-badge">
            {unread}
          </span>
        )}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} aria-hidden="true" />
          <div className={`absolute w-80 max-w-[85vw] bg-white border border-zinc-200 rounded-sm shadow-xl z-50 ${placement === "up" ? "left-0 bottom-full mb-2" : "right-0 mt-2"}`} data-testid="notif-panel">
            <div className="px-4 py-2 border-b border-zinc-100 flex items-center justify-between gap-2">
              <span className="label-caps">{t("notif.title")}</span>
              {pushSupported() && perm !== "granted" && (
                <button onClick={doEnable} className="text-xs text-[#0052FF] hover:underline" data-testid="notif-enable-push">
                  {t("notif.enablePush")}
                </button>
              )}
              {pushSupported() && perm === "granted" && (
                <button onClick={doTest} className="text-xs text-[#0052FF] hover:underline" data-testid="notif-test-push">
                  {t("notif.test")}
                </button>
              )}
            </div>
            <div className="max-h-96 overflow-y-auto divide-y divide-zinc-100">
              {items.length === 0 && (
                <div className="px-4 py-6 text-center text-sm text-zinc-500" data-testid="notif-empty">{t("notif.empty")}</div>
              )}
              {items.map((n) => (
                <a key={n.id} href={n.url || "#"} className="block px-4 py-3 hover:bg-zinc-50" onClick={() => setOpen(false)} data-testid="notif-item">
                  <div className="text-sm font-medium">{n.title}</div>
                  <div className="text-xs text-zinc-600">{n.body}</div>
                  <div className="text-[10px] text-zinc-400 mt-0.5 font-mono-num">{(n.created_at || "").slice(0, 16).replace("T", " ")}</div>
                </a>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
